import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';

// Dot fill by state, extracted to avoid nested ternaries in the JSX.
const dotBg = (done, active) => {
  if (done) return 'gold.main';
  if (active) return 'gold.soft';
  return 'surface.overlay';
};

/**
 * OTRIX Play — onboarding progress rail (ported from the MySchoolUAE FilterSchool sidebar,
 * dressed in the OTRIX monochrome + gold system). A vertical track with a gold progress fill,
 * one dot per step (check = done, gold ring = active), plus the current step's icon + question.
 */
function OnboardingSidebar({ steps, currentStep, icon = null, question, hint = '' }) {
  const count = Math.max(1, steps.length - 1);
  const progress = Math.min(1, Math.max(0, currentStep / count));

  return (
    <Stack direction="row" spacing={2.5} sx={{ height: '100%' }}>
      {/* Vertical track + dots */}
      <Box sx={{ position: 'relative', width: 12, flex: 'none' }}>
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 4,
            bottom: 4,
            width: 2,
            transform: 'translateX(-50%)',
            bgcolor: 'hairline.main',
            borderRadius: 1
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 4,
            width: 2,
            height: `calc(${progress * 100}% - 8px)`,
            transform: 'translateX(-50%)',
            bgcolor: 'gold.main',
            borderRadius: 1,
            transition: 'height 320ms cubic-bezier(0.32,0.72,0,1)'
          }}
        />
        <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="space-between">
          {steps.map((s, i) => {
            const done = i < currentStep;
            const active = i === currentStep;
            return (
              <Box
                key={s}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: dotBg(done, active),
                  border: '2px solid',
                  borderColor: done || active ? 'gold.main' : 'hairline.strong',
                  transition: 'all 220ms cubic-bezier(0.32,0.72,0,1)'
                }}>
                {done && <Check sx={{ fontSize: 8, color: 'ink.main' }} />}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Current step: icon + question */}
      <Stack spacing={1.5} justifyContent="center" sx={{ flex: 1 }}>
        <Box sx={{ color: 'gold.main', '& svg': { fontSize: 34 } }}>{icon}</Box>
        <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em">
          {question}
        </Typography>
        {hint && (
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

OnboardingSidebar.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentStep: PropTypes.number.isRequired,
  icon: PropTypes.node,
  question: PropTypes.string.isRequired,
  hint: PropTypes.string
};

export default OnboardingSidebar;
